import { useState, useEffect } from 'react';
import { useWeb3Context } from '../context/Web3Context';
import { useWallet } from '@solana/wallet-adapter-react';

/**
 * Custom hook to check if the current connected wallet is an admin
 * Checks both BSC (MetaMask) and Solana wallet addresses against admin addresses from env
 */
export const useAdminAuth = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get BSC wallet connection from Web3Context
  const { address: bscAddress } = useWeb3Context();
  
  // Get Solana wallet connection
  const { publicKey: solanaPublicKey, connected: solanaConnected } = useWallet();
  
  // Get admin addresses from environment variables
  const adminAddresses = {
    bsc: [
      process.env.REACT_APP_BSC_ADMIN_WALLET_ADDRESS,
      process.env.REACT_APP_ADMIN_BSC_ADDRESS_1,
      process.env.REACT_APP_ADMIN_BSC_ADDRESS_2,
      process.env.REACT_APP_ADMIN_BSC_ADDRESS_3,
    ].filter(Boolean), // Remove undefined values
    
    solana: [
      process.env.REACT_APP_SOLANA_ADMIN_WALLET_ADDRESS,
      process.env.REACT_APP_ADMIN_SOLANA_ADDRESS_1,
      process.env.REACT_APP_ADMIN_SOLANA_ADDRESS_2,
      process.env.REACT_APP_ADMIN_SOLANA_ADDRESS_3,
    ].filter(Boolean), // Remove undefined values
  };

  useEffect(() => {
    const checkAdminStatus = () => {
      setIsLoading(true);
      
      try {
        let adminCheck = false;
        
        // Check BSC address if connected
        if (bscAddress) {
          const isBscAdmin = adminAddresses.bsc.some(
            adminAddr => adminAddr && adminAddr.toLowerCase() === bscAddress.toLowerCase()
          );
          if (isBscAdmin) {
            adminCheck = true;
          }
        }
        
        // Check Solana address if connected
        if (solanaConnected && solanaPublicKey) {
          const solanaAddress = solanaPublicKey.toString();
          const isSolanaAdmin = adminAddresses.solana.some(
            adminAddr => adminAddr && adminAddr === solanaAddress
          );
          if (isSolanaAdmin) {
            adminCheck = true;
          }
        }
        
        setIsAdmin(adminCheck);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [bscAddress, solanaPublicKey, solanaConnected, adminAddresses.bsc, adminAddresses.solana]);

  return {
    isAdmin,
    isLoading,
    adminAddresses,
    hasWalletConnected: !!(bscAddress || (solanaConnected && solanaPublicKey))
  };
};
